import { useEffect, useState } from 'react';
import { useEventRealtime } from './useEventRealtime';

/**
 * In-play statuses across Plaee's two vocabularies: the REST feed says
 * `live`, the realtime channels say `IN_PROGRESS`. Case-insensitive.
 */
const LIVE_STATUSES = new Set(['live', 'in_progress']);

/** True when a Plaee `realWorldEventStatus` value means "in play". */
export function isLiveStatus(status: string): boolean {
  return LIVE_STATUSES.has(status.toLowerCase());
}

/**
 * A card's live (in-play) state: seeded from the REST `isLive`, flipped in
 * real time by `sport_event_updated` on the per-event channel — so an event
 * going live swaps every rendered card to the live design without a reload,
 * and the indication clearing reverts them (spec §7).
 *
 * Re-seeds when the REST value changes (query refetch), which also self-heals
 * a missed socket message. Pass a null `eventKey` to skip the subscription
 * entirely (REGULAR events never go live) — the seed is returned as-is.
 */
export function useEventLiveStatus(
  internalEventId: string,
  eventKey: string | null | undefined,
  seedIsLive: boolean,
): boolean {
  const [live, setLive] = useState(seedIsLive);

  useEffect(() => {
    setLive(seedIsLive);
  }, [seedIsLive]);

  useEventRealtime(eventKey ? internalEventId : null, eventKey, {
    onSportEventUpdated: (e) => setLive(isLiveStatus(e.realWorldEventStatus)),
  });

  return live;
}
